import React, { useEffect, useState } from "react";
import styles from "./province-list.module.css";
import CustomTable from "../../../../common/custom-table/custom-table";
import { useNavigate } from "react-router-dom";
import CustomTag from "../../../../common/custom-tag/custom-tag";
import CustomProfileAvatar from "../../../../common/custom-profile-avatar/custom-profile-avatar";
import { Popconfirm, Typography } from "antd";
import RoutesConstant from "../../../../constants/routes";
import {
  deleteInstitution,
  getInstitutions,
} from "../../../../services/institution";
import { toast } from "react-toastify";
import { getInstitutionCount } from "../../../../services/dashboard";
import CustomButton from "../../../../common/custom-button/custom-button";
import SVGIcon from "../../../../constants/SVGIcon";
import { Country, State } from "country-state-city";
import CustomFilter from "../../../../components/custom-filter/custom-filter";
import {
  curriculumType,
  ownershipStatusType,
  schoolType,
} from "../../../../constants/common";

const ProvinceList = ({ selectedCountry }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [expandLoading, setExpandLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [institutionList, setInstitutionList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState("");
  const [filterValues, setFilterValues] = useState({
    status: [],
  });

  const filterData = [
    {
      id: "curriculum",
      label: "Curriculum",
      data: curriculumType?.map((item) => item?.value),
    },
    {
      id: "school_type",
      label: "School Type",
      data: schoolType?.map((item) => item?.value),
    },
    {
      id: "ownership_type",
      label: "Ownership Type",
      data: ownershipStatusType,
    },
  ];

  const columns = [
    {
      title: "Province",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: (value) =>
        value ? (
          <CustomTag
            text={value}
            color={"#39b54a"}
            background={"rgba(57, 181, 74, 0.1)"}
            borderColor={"#39b54a"}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Total Schools",
      dataIndex: "schools",
      key: "schools",
    },
  ];

  const expandColumns = [
    {
      title: "Institution",
      dataIndex: "institution_name",
      key: "institution_name",
      render: (value, record) => (
        <div
          className={
            record?.logo
              ? styles.nameContainer
              : styles.nameContainerWithoutLogo
          }
        >
          <CustomProfileAvatar
            image={
              record?.institution_logo
                ? record?.institution_logo
                : "sample-school.jpg"
            }
            imageName={record?.institution_name}
            imageSrc={
              record?.institution_logo ?? "assets/images/sample-school.jpg"
            }
            divBackgroundColor={"#97a997"}
          />
          <Typography>{value}</Typography>
        </div>
      ),
    },
    {
      title: "Location",
      dataIndex: "physical_address",
      key: "physical_address",
      render: (value) => value || "-",
      width: "40%",
    },
    {
      title: "Total Students",
      dataIndex: "studentsCount",
      key: "studentsCount",
      render: (value) => <span> {value ?? 0}</span>,
    },
    {
      title: "Total Teachers",
      dataIndex: "teachersCount",
      key: "teachersCount",
      render: (value) => <span> {value ?? 0}</span>,
    },
  ];

  const menuItems = (record) => [
    {
      key: "1",
      label: "View Details",
      onClick: () =>
        navigate(`${RoutesConstant.INSTITUTION_DETAILS}?uid=${record?.id}`),
    },
    {
      key: "2",
      label: "Update",
      onClick: () => onUpdateDetails(record),
    },
    {
      key: "3",
      label: (
        <Popconfirm
          title={`Are you sure you want to delete ${record?.institution_name}?`}
          onConfirm={() => handleDeleteInstitution(record?.id)}
          okText="Yes"
          cancelText="No"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
    },
  ];

  useEffect(() => {
    configureStateList(selectedCountry);
  }, [selectedCountry, filterValues]);

  const getInstitutionList = async (
    tempPage = pageNo,
    tempPageSize = pageSize,
    tempProvince = selectedProvince
  ) => {
    try {
      setExpandLoading(true);
      const payload = {
        // page: tempPage,
        // pageSize: tempPageSize,
        province: tempProvince,
      };

      Object.entries(filterValues).forEach(([key, valueArray]) => {
        if (Array.isArray(valueArray) && valueArray.length > 0) {
          payload[key] = valueArray[0];
        }
      });

      const response = await getInstitutions(payload);
      if (response?.data) {
        setInstitutionList(response?.data?.data?.schools);
        setTotalCount(response?.data?.data?.totalRecords);
      }
      setExpandLoading(false);
    } catch (error) {
      setExpandLoading(false);
      console.log("error", error);
    }
  };

  const handleDeleteInstitution = async (institution_id) => {
    try {
      setLoading(true);
      const response = await deleteInstitution(institution_id);
      if (response?.status) {
        toast.success("Institution Deleted Successfully !");
        getInstitutionList();
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.failure("An error occured, please try again.");
      console.log("error", error);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getInstitutionList(page, pageSize);
  };

  const onRowExpand = (record) => {
    console.log(record);
    setSelectedProvince(record?.name);
    getInstitutionList(pageNo, pageSize, record?.name);
  };

  const configureStateList = async (value) => {
    try {
      setLoading(true);
      const tempStateList = State.getStatesOfCountry(value);
      // get selected country data
      const tempCountryList = Country.getAllCountries();
      const countryData = tempCountryList?.length
        ? tempCountryList.filter((e) => e?.isoCode === value)
        : [];

      let tempSchoolCountData = [];

      const payload = {};
      Object.entries(filterValues).forEach(([key, valueArray]) => {
        if (Array.isArray(valueArray) && valueArray.length > 0) {
          payload[key] = valueArray[0];
        }
      });

      // get school count based on province
      const response = await getInstitutionCount(payload);
      if (response?.data?.data?.provinceCounts?.length) {
        tempSchoolCountData = response?.data?.data?.provinceCounts;
      }
      // modify province data with school count
      const modifiedStateList = tempStateList.map((e, i) => {
        const filterProvince = tempSchoolCountData?.filter(
          (el) => el?.province === e?.name
        );

        return {
          ...e,
          key: i + 1,
          country: countryData?.length ? countryData[0]?.name : "-",
          schools: filterProvince?.length
            ? filterProvince[0]?.province_count
            : 0,
        };
      });
      setStateList(modifiedStateList);
      if (selectedProvince) getInstitutionList();
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const handleAddInstitutions = () => {
    navigate(RoutesConstant.INSTITUTION_FORM);
  };

  const onUpdateDetails = (record) => {
    navigate(`${RoutesConstant.INSTITUTION_FORM}?uid=${record?.id}`);
  };

  const onChangeFilter = (id, values) => {
    const existingValues = filterValues[id] || [];
    const removedValue = existingValues.find((val) => !values.includes(val));
    const addedValue = values.find((val) => !existingValues.includes(val));

    if (removedValue) {
      const updatedValues = existingValues.filter(
        (val) => val !== removedValue
      );
      setFilterValues((prev) => ({
        ...prev,
        [id]: updatedValues,
      }));
    } else if (addedValue) {
      setFilterValues((prev) => ({
        ...prev,
        [id]: [addedValue],
      }));
    }
  };

  const onFilterReset = () => {
    setFilterValues({
      status: [],
    });
  };

  const expandedRowRender = () => (
    <CustomTable
      columns={expandColumns}
      dataSource={institutionList}
      total={totalCount}
      loading={expandLoading}
      pageSize={pageSize}
      currentPage={pageNo}
      showMoreIcon={true}
      pagination={false}
      menuItems={menuItems}
      handlePageChange={handleChangePage}
    />
  );

  return (
    <div>
      <div className={styles.headerContainer}>
        <Typography className={styles.title}>Institutions</Typography>
        <div className={styles.filterContainer}>
          <CustomFilter
            height="36px"
            filterData={filterData}
            values={filterValues}
            onResetFilter={onFilterReset}
            onChange={(id, values) => onChangeFilter(id, values)}
          />
          <CustomButton
            className={styles.buttonStyle}
            title="Add Institutions"
            size={"large"}
            icon={
              <SVGIcon
                name={"add-icon"}
                width={"24"}
                height={"24"}
                className="anticon"
                stroke={"#FFF"}
              />
            }
            onClick={handleAddInstitutions}
          />
        </div>
      </div>
      <CustomTable
        columns={columns}
        dataSource={stateList}
        pagination={false}
        collapsible={true}
        renderCollapseContent={expandedRowRender}
        handleExpand={(expand, record) => (expand ? onRowExpand(record) : null)}
        loading={loading}
      />
    </div>
  );
};

export default ProvinceList;
